import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataService } from '@/lib/service-context';

export interface QuickLink {
  /** Key from QUICK_LINK_CATALOG identifying the target page. */
  key: string;
  /** Keyboard shortcut in normalized form (e.g. "ctrl+shift+a"). Empty string = no shortcut. */
  shortcut: string;
}

const STORAGE_KEY = 'erp_quick_links_v1';
const MAX_QUICK_LINKS = 8;

function loadLocal(): QuickLink[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed
      .filter((x) => x && typeof x.key === 'string' && typeof x.shortcut === 'string')
      .slice(0, MAX_QUICK_LINKS);
  } catch {
    return null;
  }
}

export function useQuickLinks() {
  const ds = useDataService();
  const loaded = useRef(false);
  const [links, setLinks] = useState<QuickLink[]>(() => loadLocal() ?? []);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    ds.userPreferences
      .get(STORAGE_KEY)
      .then((val) => {
        if (Array.isArray(val)) {
          const sanitized = val
            .filter((x): x is QuickLink =>
              !!x && typeof (x as QuickLink).key === 'string' && typeof (x as QuickLink).shortcut === 'string',
            )
            .slice(0, MAX_QUICK_LINKS);
          setLinks(sanitized);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
        }
      })
      .catch(() => {});
  }, [ds]);

  const persist = useCallback(
    (next: QuickLink[]) => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      ds.userPreferences.set(STORAGE_KEY, next).catch(() => {});
    },
    [ds],
  );

  const add = useCallback(
    (key: string, shortcut = '') => {
      setLinks((prev) => {
        if (prev.some((l) => l.key === key)) return prev;
        if (prev.length >= MAX_QUICK_LINKS) return prev;
        const next = [...prev, { key, shortcut: normalizeShortcut(shortcut) }];
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const remove = useCallback(
    (key: string) => {
      setLinks((prev) => {
        const next = prev.filter((l) => l.key !== key);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const setShortcut = useCallback(
    (key: string, shortcut: string) => {
      setLinks((prev) => {
        const normalized = normalizeShortcut(shortcut);
        const next = prev.map((l) => {
          if (l.key === key) return { ...l, shortcut: normalized };
          // Clear conflicting shortcut on other links.
          if (normalized && l.shortcut === normalized) return { ...l, shortcut: '' };
          return l;
        });
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const move = useCallback(
    (from: number, to: number) => {
      setLinks((prev) => {
        if (from < 0 || from >= prev.length || to < 0 || to >= prev.length) return prev;
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved!);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  return { links, add, remove, setShortcut, move, maxLinks: MAX_QUICK_LINKS };
}

/** Canonicalize a shortcut string so comparisons are stable. */
export function normalizeShortcut(raw: string): string {
  if (!raw) return '';
  const parts = raw
    .toLowerCase()
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean);
  const mods = new Set<string>();
  let key = '';
  for (const p of parts) {
    if (p === 'ctrl' || p === 'control') mods.add('ctrl');
    else if (p === 'alt' || p === 'option') mods.add('alt');
    else if (p === 'shift') mods.add('shift');
    else if (p === 'meta' || p === 'cmd' || p === 'command') mods.add('meta');
    else key = p;
  }
  if (!key) return '';
  const order = ['ctrl', 'alt', 'shift', 'meta'];
  return [...order.filter((m) => mods.has(m)), key].join('+');
}

/** Build the normalized shortcut string from a keyboard event. */
export function eventToShortcut(e: KeyboardEvent): string {
  const key = e.key;
  // Skip pure modifier presses.
  if (['Control', 'Alt', 'Shift', 'Meta'].includes(key)) return '';
  const mods: string[] = [];
  if (e.ctrlKey) mods.push('ctrl');
  if (e.altKey) mods.push('alt');
  if (e.shiftKey) mods.push('shift');
  if (e.metaKey) mods.push('meta');
  let k = key.toLowerCase();
  if (k === ' ') k = 'space';
  return [...mods, k].join('+');
}

/** Human-readable display string (e.g. "Ctrl + Shift + A"). */
export function formatShortcut(shortcut: string): string {
  if (!shortcut) return '';
  return shortcut
    .split('+')
    .map((p) => {
      if (p === 'ctrl') return 'Ctrl';
      if (p === 'alt') return 'Alt';
      if (p === 'shift') return 'Shift';
      if (p === 'meta') return '⌘';
      if (p === 'space') return 'Space';
      if (p.length === 1) return p.toUpperCase();
      return p.charAt(0).toUpperCase() + p.slice(1);
    })
    .join(' + ');
}
